import { memo } from "react";
import { EveOfferOverride, PartnershipTier } from "@/types/types";
import { currencySymbol } from "@/lib/format";

interface TierColumnCellProps {
  currentTierOrder?: number;
  tiers?: PartnershipTier[];
  status?: string;
  negotiatedPrice?: string;
  currency?: string;
  /** Amy-v2: the team's offer for the creator's current tier (eve_offer_overrides) — what Amy quotes. */
  offer?: EveOfferOverride;
}

const PAID_TYPES = new Set(["max_budget", "max_cpm", "custom_formula"]);

/** Mirrors amy tierWithOffer (lib/domain/offerOverride.ts): the team's fee / revenue share / CPA, the tier's fixed extras. */
function tierWithOffer(tier: PartnershipTier, o: EveOfferOverride): PartnershipTier {
  const paid = tier.components.find((c) => PAID_TYPES.has(c.type));
  const components: PartnershipTier["components"] = [];
  if (o.max_fee != null) components.push({ type: "max_budget", value: o.max_fee, description: "" });
  else if (o.keep_campaign_fee && paid) components.push(paid);
  if (o.revenue_share != null) components.push({ type: "revenue_share", value: o.revenue_share, description: "" });
  if (o.cpa != null) components.push({ type: "cpa", value: o.cpa, description: "" });
  for (const c of tier.components) {
    if (!PAID_TYPES.has(c.type) && c.type !== "revenue_share" && c.type !== "cpa") components.push(c);
  }
  return { ...tier, components };
}

// Mirrored by _summarize_tier in
// lambdas/outreach_lambda/utils/substitute_activation_variables.py — keep in sync.
export function summarizeTier(
  tier: PartnershipTier,
  negotiatedPrice?: string,
  currency?: string,
): string {
  const symbol = currencySymbol(currency);
  const parts: string[] = [];
  for (const c of tier.components) {
    switch (c.type) {
      case "max_budget":
        parts.push(`Paid: ${symbol}${negotiatedPrice ?? " ???"}`);
        break;
      case "max_cpm":
        parts.push(
          `Paid: ${symbol}${negotiatedPrice ?? " ???"}${
            c.value != null ? ` (${symbol}${c.value} CPM)` : ""
          }`
        );
        break;
      case "custom_formula":
        parts.push(`Paid: ${symbol}${negotiatedPrice ?? " ???"}`);
        break;
      case "cpa":
        parts.push(c.value ? `CPA ${symbol}${c.value}` : "CPA");
        break;
      case "revenue_share":
        parts.push(c.value ? `${c.value}% Rev Share` : "Rev Share");
        break;
      case "performance_cpm":
        parts.push("Perf CPM");
        break;
      case "other":
        parts.push("Other");
        break;
    }
  }
  return parts.join(" + ");
}

function TierColumnCell({
  currentTierOrder,
  tiers,
  status,
  negotiatedPrice,
  currency,
  offer,
}: TierColumnCellProps) {
  const hideStatuses = ["pending", "pending-outreach", "to-contact", "outreached"];
  const shouldHide = !!status && hideStatuses.includes(status) && !offer;

  if (!currentTierOrder || !tiers?.length || shouldHide) {
    return (
      <span className="text-muted-foreground text-xs italic">missing</span>
    );
  }

  const campaignTier = tiers.find((t) => t.order === currentTierOrder);
  if (!campaignTier) {
    return (
      <span className="text-muted-foreground text-xs italic">missing</span>
    );
  }

  // The team's offer replaces the tier's fee / revenue share / CPA; their max
  // fee stands in for the price until one is agreed.
  const tier = offer ? tierWithOffer(campaignTier, offer) : campaignTier;
  const price = negotiatedPrice ?? (offer?.max_fee != null ? `${offer.max_fee} max` : undefined);
  const summary = summarizeTier(tier, price, currency);

  return (
    // Clipped to the column: a combined tier's summary ("Paid: $2000 ($40 CPM)
    // + 20% Rev Share") is wider than the cell, and table cells don't clip —
    // it used to spill over the next column. The title carries the full text.
    <div
      className="flex min-w-0 max-w-full flex-col items-start justify-center overflow-hidden pl-1.5 border-l border-dashed"
      title={`Tier ${tier.order} — ${summary}`}
    >
      <span className="text-muted-foreground font-mono uppercase text-[11px]">
        Tier {tier.order}
        {offer ? " · custom" : ""}
      </span>
      <span className="block max-w-full truncate text-sm">{summary}</span>
    </div>
  );
}

export default memo(TierColumnCell);
